import React, { useState } from "react";
import {
  Code2,
  Copy,
  Check,
  Smartphone,
  ExternalLink,
  ShieldCheck,
  Send,
  Zap,
  Sparkles,
} from "lucide-react";
import { MlcFeedbackWidget } from "../widget/MlcFeedbackWidget";
import { UserProfile } from "../types";
import { auth } from "../services/firebase";

export interface WidgetDemoViewProps {
  currentUser: UserProfile;
}

const REGISTERED_APPS = [
  { id: "operations-hub", name: "Operations Hub", dept: "Operations" },
  { id: "mlc-hr-tools", name: "HR Tools", dept: "Human Resources" },
  { id: "viq-autostart", name: "VIQ Autostart", dept: "Engineering" },
  { id: "memoq-bridge", name: "memoQ Bridge", dept: "Translation Tech" },
  { id: "teppi-portal", name: "Teppi Portal", dept: "Customer Success" },
  { id: "sales-pipeline", name: "Sales Pipeline", dept: "Sales" },
];

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const WidgetDemoView: React.FC<WidgetDemoViewProps> = ({
  currentUser,
}) => {
  const [appName, setAppName] = useState("Operations Hub");
  const [appId, setAppId] = useState("operations-hub");
  const [isAppIdManuallyEdited, setIsAppIdManuallyEdited] = useState(false);
  const [position, setPosition] = useState<
    "bottom-right" | "bottom-left" | "top-right" | "top-left"
  >("bottom-right");
  const [activeSnippetTab, setActiveSnippetTab] = useState<
    "react" | "vanilla" | "api" | "prompt"
  >("react");
  const [copied, setCopied] = useState(false);

  const effectiveAppName = appName.trim() || "My Application";
  const effectiveAppId = appId.trim() || "my-app";

  const handleAppNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setAppName(val);
    if (!isAppIdManuallyEdited) {
      setAppId(slugify(val));
    }
  };

  const handleAppIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAppIdManuallyEdited(true);
    setAppId(e.target.value);
  };

  const handleSelectPreset = (presetName: string, presetId: string) => {
    setAppName(presetName);
    setAppId(presetId);
    setIsAppIdManuallyEdited(false);
  };

  const reactSnippet = `import React from "react";
import { getAuth } from "firebase/auth";
import { MlcFeedbackWidget } from "@mlconnections/bug-tracking-widget";

export function AppLayout() {
  const auth = getAuth();
  // Provide the authenticated user session from your auth provider
  const user = {
    email: authUser.email,             // e.g. "user@mlconnections.com"
    displayName: authUser.displayName, // e.g. "Jane Doe"
    uid: authUser.uid,
  };

  return (
    <div className="min-h-screen">
      {/* Host application layout & views */}
      <main className="p-6">
        <h1>${effectiveAppName}</h1>
      </main>

      {/* Embeddable MLC Feedback Widget */}
      <MlcFeedbackWidget
        appId="${effectiveAppId}"
        appName="${effectiveAppName}"
        position="${position}"
        currentUser={user}
        getAuthToken={async () => (auth.currentUser ? auth.currentUser.getIdToken() : null)}
      />
    </div>
  );
}`;

  const vanillaSnippet = `<!-- 1. Include the MLC Feedback Widget script bundle -->
<script src="https://mlc-bug-tracking.web.app/widget.js" async></script>

<!-- 2. Initialize in your application's bootstrap script -->
<script>
  window.addEventListener("DOMContentLoaded", function () {
    window.initMlcFeedbackWidget({
      appId: "${effectiveAppId}",
      appName: "${effectiveAppName}",
      position: "${position}",
      // Provide the authenticated user session from your auth provider
      currentUser: {
        email: sessionUser.email,          // e.g. "user@mlconnections.com"
        displayName: sessionUser.displayName  // e.g. "Jane Doe"
      },
      getAuthToken: async function() {
        return firebase.auth().currentUser ? firebase.auth().currentUser.getIdToken() : null;
      }
    });
  });
</script>`;

  const apiSnippet = `# Submit an issue directly to the MLC Ticketing REST API
# Requires a valid Firebase ID token in the Authorization header
curl -X POST https://bt-api-qbuhtyitjq-uc.a.run.app/api/v1/issues \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <FIREBASE_ID_TOKEN>" \\
  -H "X-Caller-Email: user@mlconnections.com" \\
  -d '{
    "appId": "${effectiveAppId}",
    "appName": "${effectiveAppName}",
    "title": "Issue title summary",
    "category": "bug",
    "priority": "high",
    "description": "Steps to reproduce and issue details...",
    "reporterEmail": "user@mlconnections.com",
    "reporterName": "Jane Doe"
  }'`;

  const promptSnippet = `Please install the MLC Feedback & Ticketing Widget into this application by pulling the widget component from the GitHub repository "multilingual-connections/bug-tracking".

### Instructions:
1. **Pull Source Files from GitHub**:
   Retrieve the widget source files from the \`multilingual-connections/bug-tracking\` repository (\`main\` branch):
   - \`app/src/widget/MlcFeedbackWidget.tsx\`
   - \`app/src/widget/widgetService.ts\`
   - \`app/src/widget/index.ts\`
   - \`app/src/utils/appDetection.ts\`

   Place all 4 files inside this application under \`src/components/feedbackWidget/\` (or equivalent components directory).

   *Ways to retrieve the files:*
   - **Option A (GitHub CLI)**:
     \`\`\`bash
     mkdir -p src/components/feedbackWidget
     gh api repos/multilingual-connections/bug-tracking/contents/app/src/widget/MlcFeedbackWidget.tsx?ref=main -H "Accept: application/vnd.github.v3.raw" > src/components/feedbackWidget/MlcFeedbackWidget.tsx
     gh api repos/multilingual-connections/bug-tracking/contents/app/src/widget/widgetService.ts?ref=main -H "Accept: application/vnd.github.v3.raw" > src/components/feedbackWidget/widgetService.ts
     gh api repos/multilingual-connections/bug-tracking/contents/app/src/widget/index.ts?ref=main -H "Accept: application/vnd.github.v3.raw" > src/components/feedbackWidget/index.ts
     gh api repos/multilingual-connections/bug-tracking/contents/app/src/utils/appDetection.ts?ref=main -H "Accept: application/vnd.github.v3.raw" > src/components/feedbackWidget/appDetection.ts
     \`\`\`
   - **Option B (Shallow git clone)**:
     \`\`\`bash
     git clone --depth 1 https://github.com/multilingual-connections/bug-tracking.git temp-bt
     mkdir -p src/components/feedbackWidget
     cp temp-bt/app/src/widget/* src/components/feedbackWidget/
     cp temp-bt/app/src/utils/appDetection.ts src/components/feedbackWidget/appDetection.ts
     rm -rf temp-bt
     \`\`\`

2. **Adjust Imports & Dependencies**:
   - Ensure \`MlcFeedbackWidget.tsx\` imports \`detectSourceApp\` from \`./appDetection\`.
   - Use \`import type\` for type-only imports (\`UserIssueSummary\`, \`IssueCommentSummary\`) if \`verbatimModuleSyntax\` is enabled.
   - Verify \`lucide-react\` is present in \`package.json\` (install it if missing).
   - Telemetry Notice: Do NOT display any "Diagnostic Telemetry" accordion or collapsible UI to the user. All diagnostic metadata (URL, browser, OS, viewport) is automatically and silently captured in the background payload upon submission.

3. **CSP (Content Security Policy) Requirement**:
   If the host application defines CSP headers (in \`firebase.json\`, server headers, or \`<meta http-equiv="Content-Security-Policy">\`), you **must** add \`https://*.run.app\` to the \`connect-src\` directive:
   \`\`\`
   connect-src ... https://*.run.app ...;
   \`\`\`
   Without this, modern browsers will block the widget from reaching \`bt_api\`.

4. **Mount in Root Layout**:
   In the main application root/layout component (e.g. \`App.tsx\` or main layout):
   - Import \`<MlcFeedbackWidget />\` from \`./components/feedbackWidget\`.
   - Mount it at the root of the layout with:
     \`\`\`tsx
     <MlcFeedbackWidget
       appId="${effectiveAppId}"
       appName="${effectiveAppName}"
       position="${position}"
       currentUser={{
         email: currentUser?.email,
         displayName: currentUser?.displayName || currentUser?.name,
       }}
       getAuthToken={async () => auth.currentUser ? auth.currentUser.getIdToken() : null}
     />
     \`\`\`
   - Wire \`currentUser\` and \`getAuthToken\` to the active Firebase Auth state in this application.

5. **Verify & Deliver**:
   - Follow standard git workflow: do not commit directly to \`main\`/\`master\`, work on a \`feature/\` branch.
   - Run typecheck and production build to verify zero errors before committing and pushing.`;

  const getCurrentSnippet = () => {
    switch (activeSnippetTab) {
      case "vanilla":
        return vanillaSnippet;
      case "api":
        return apiSnippet;
      case "prompt":
        return promptSnippet;
      case "react":
      default:
        return reactSnippet;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getCurrentSnippet());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner - Light Colored Theme */}
      <div className="bg-white text-slate-900 rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 text-xs font-semibold uppercase tracking-wider mb-2">
            <Zap className="w-3.5 h-3.5" />
            <span>Widget Integration Hub</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Embeddable Feedback Widget
          </h1>
          <p className="text-slate-600 text-sm mt-1 max-w-2xl">
            Drop unified, in-app bug and feedback submission into any connected
            MLC child application (Operations Hub, HR Tools, VIQ, etc.) with
            automated Chicago-hours SLA tracking and zero auth pollution.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://bt-api-qbuhtyitjq-uc.a.run.app/health"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-xl border border-slate-200 shadow-sm transition"
          >
            <span>API Health</span>
            <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
          </a>
        </div>
      </div>

      {/* Main Configuration & Snippets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Interactive Configuration */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-5">
            <h2 className="font-bold text-base text-slate-900 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#2ba5b5]" />
              <span>Widget Configuration</span>
            </h2>

            {/* Application Details Fields */}
            <div className="space-y-3">
              <span className="block text-xs font-semibold text-slate-700">
                Target Child Application
              </span>

              <div>
                <label
                  htmlFor="target-app-name"
                  className="block text-xs font-medium text-slate-700 mb-1"
                >
                  Application Name
                </label>
                <input
                  id="target-app-name"
                  type="text"
                  value={appName}
                  onChange={handleAppNameChange}
                  placeholder="e.g. Operations Hub"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ba5b5]"
                />
              </div>

              <div>
                <label
                  htmlFor="target-app-id"
                  className="block text-xs font-medium text-slate-700 mb-1"
                >
                  Application ID
                </label>
                <input
                  id="target-app-id"
                  type="text"
                  value={appId}
                  onChange={handleAppIdChange}
                  placeholder="e.g. operations-hub"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ba5b5]"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Unique slug for ticket routing, SLA engines, and telemetry.
                </p>
              </div>

              {/* Quick-fill Presets */}
              <div className="pt-1">
                <span className="block text-[11px] font-semibold text-slate-500 mb-1.5">
                  Quick-fill Presets:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {REGISTERED_APPS.map((app) => (
                    <button
                      key={app.id}
                      type="button"
                      onClick={() => handleSelectPreset(app.name, app.id)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-medium transition cursor-pointer ${
                        appId === app.id && appName === app.name
                          ? "bg-teal-50 border-teal-300 text-teal-800 font-semibold"
                          : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                      }`}
                    >
                      {app.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Position Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Floating Button Position
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { id: "bottom-right", label: "Bottom Right" },
                    { id: "bottom-left", label: "Bottom Left" },
                    { id: "top-right", label: "Top Right" },
                    { id: "top-left", label: "Top Left" },
                  ] as const
                ).map((pos) => (
                  <button
                    key={pos.id}
                    type="button"
                    onClick={() => setPosition(pos.id)}
                    className={`py-2 px-3 rounded-xl border text-xs font-medium transition text-center ${
                      position === pos.id
                        ? "bg-teal-50 border-teal-300 text-teal-800 font-semibold shadow-sm"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {pos.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Features Checklist */}
            <div className="pt-2 border-t border-slate-100 space-y-2 text-xs text-slate-600">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>Zero Custom Claims & Auth Pollution</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>Automated Silent Diagnostic Telemetry</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>Clipboard Image Paste (Ctrl+V) & Dropzone</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>Chicago Business Hours SLA Engine Enforced</span>
              </div>
            </div>
          </div>

          {/* Quick Testbed Prompt Card */}
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 text-teal-900">
            <h3 className="font-bold text-sm mb-1 flex items-center gap-1.5">
              <Send className="w-4 h-4 text-[#2ba5b5]" />
              <span>Live Testbed Active</span>
            </h3>
            <p className="text-xs text-teal-800 leading-relaxed">
              The floating widget button is active on this screen at{" "}
              <strong>{position.replace("-", " ")}</strong>. Click it to submit
              test reports or view tickets for{" "}
              <strong>{effectiveAppName}</strong>.
            </p>
          </div>
        </div>

        {/* Right Column: Code Snippets & Docs */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            {/* Snippet Header & Tabs */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50/80">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-teal-600" />
                <span className="text-xs font-bold text-slate-800">
                  Integration Snippet
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab("react")}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    activeSnippetTab === "react"
                      ? "bg-[#2ba5b5] text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  React
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab("vanilla")}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    activeSnippetTab === "vanilla"
                      ? "bg-[#2ba5b5] text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  HTML / Script
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab("api")}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    activeSnippetTab === "api"
                      ? "bg-[#2ba5b5] text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  REST API
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab("prompt")}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    activeSnippetTab === "prompt"
                      ? "bg-[#2ba5b5] text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span>Antigravity Prompt</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopy}
                  className="ml-3 flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-medium transition cursor-pointer shadow-sm"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-700 font-semibold">
                        Copied!
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Code Content */}
            <div className="p-5 font-mono text-xs text-slate-800 overflow-x-auto leading-relaxed bg-slate-50/70 border-t border-slate-200 max-h-[460px]">
              <pre className="whitespace-pre-wrap">{getCurrentSnippet()}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* Render the live interactive widget for the demo */}
      <MlcFeedbackWidget
        key={`${effectiveAppId}-${effectiveAppName}-${position}`}
        appId={effectiveAppId}
        appName={effectiveAppName}
        position={position}
        currentUser={{
          email: currentUser.email,
          displayName: currentUser.displayName,
          uid: currentUser.uid,
        }}
        getAuthToken={async () =>
          auth.currentUser ? auth.currentUser.getIdToken() : null
        }
      />
    </div>
  );
};
